import type { rechargeType } from '@/types/viewsType/rechargeType'
import { getResult, apiChicChargePopup } from '@/api/recharge'
import { apiLuckyTrigger } from '@/api/public'
import * as Sentry from '@sentry/nuxt'

export const useRechargeStore = defineStore({
  id: 'recharge',
  state: () => ({
    rechargeList: {} as Record<number, rechargeType[]>, //所有用户的订单{ uid:[], uid:[] }
    num: 10, //一个订单限制查询次数
    uid: 0, //是0表示未登录 不进行递归查询订单
    timer: 0, //定时器
    /**  App使用的首充倒计时 豪秒*/
    appFirstCjargeTime: 0 as number
  }),
  actions: {
    /** 设置App使用的首充倒计时 */
    setAppFirstCjargeTime(v: number) {
      this.appFirstCjargeTime = v
    },
    /** 获取App使用的首充倒计时 */
    getAppFirstChargeInfo() {
      const roomStore = useRoomStore()
      if (roomStore.isApp) {
        apiChicChargePopup().then((res) => {
          if (res.code === 200) {
            this.setAppFirstCjargeTime(
              res.data ? res.data.countdownMillis || 0 : 0
            )
          }
        })
      }
    },
    // 设置uid
    setUid() {
      if (localStorage.getItem('loginInfo')) {
        this.uid = JSON.parse(localStorage.getItem('loginInfo') as string).uid
      }
    },
    // 重置uid
    resetData() {
      this.uid = 0
    },
    // 插入payermax订单记录
    setRechargeList(chargeInfo: rechargeType) {
      const uid = this.uid
      if (!this.rechargeList[uid]) {
        this.rechargeList[uid] = []
      }
      this.rechargeList[uid].push(chargeInfo)
    },
    // 查询订单结果
    requestRechargeResult() {
      const uid = this.uid
      const userStore = useUserStore()
      const publicStore = usePublicStore()
      if (this.rechargeList[uid] && this.rechargeList[uid].length && uid) {
        const chargeInfo = this.rechargeList[uid][0]
        const { orderId, chargeProdId, dollarMoney, prodName } = chargeInfo
        getResult({ chargeRecordId: orderId }).then((res) => {
          // console.log(res)
          const { data, code } = res
          if (code === 200) {
            // 0：等待，1：成功，2：失败
            switch (data) {
              case 0:
                this.num--
                if (!this.num) {
                  eventTracking.eventLog(
                    eventTracking.eventId.payment_result_waiting
                  ) // 等待埋点
                  this.deleteOrder(orderId)
                  this.num = 10
                }
                break
              case 1:
                eventTracking.eventLog(
                  eventTracking.eventId.payment_result_success
                ) // 成功埋点
                this.splitPurchaseOrder(
                  orderId,
                  dollarMoney,
                  'USD',
                  chargeProdId,
                  prodName
                )
                this.deleteOrder(orderId)
                userStore.requestUserInfo()
                // 苹果不做此功能
                if (!common.checkVersion().ios) {
                  // 充值成功幸运计数一次充值
                  apiLuckyTrigger().then((res) => {
                    if (res.code === 200) {
                      publicStore.getLuckyPopup(Boolean(res.data))
                    }
                  })
                }
                break
              case 2:
                eventTracking.eventLog(
                  eventTracking.eventId.payment_result_fail
                ) // 失败埋点
                this.rechargeList[uid].splice(
                  this.rechargeList[uid].findIndex(
                    (v) => v.orderId === orderId
                  ),
                  1
                )
                break
              default:
                //异常状态
                eventTracking.eventLog(
                  eventTracking.eventId.payment_result_abnormal
                ) // 异常埋点
                Sentry.withScope(function (scope) {
                  scope.addEventProcessor(function (event) {
                    // 向事件添加额外信息
                    event.extra = {
                      transaction_id: chargeInfo.orderId as string
                    }
                    return event
                  })
                  Sentry.captureMessage('order error')
                })
                this.deleteOrder(orderId)
                break
            }
            this.timer && window.clearTimeout(this.timer)
            this.timer = window.setTimeout(() => {
              this.requestRechargeResult()
            }, 1500)
          }
        })
      }
    },
    /**
     *  拆分购买订单事件
     *  orderId: 订单id
     *  money：金额
     *  currency: 'USD' | 'INR' 货币类型
     *  chargeProdId: 产品ID
     *  prodName： 产品名称
     *
     * GATG在拆单处理，FB埋点不做拆单，
     **/
    splitPurchaseOrder(
      orderId: string,
      dollarMoney: number,
      currency: 'USD',
      chargeProdId: string,
      prodName: string
    ) {
      const userStore = useUserStore()
      // FB埋点不做拆单，因为会有警告
      eventTracking.eventLog(eventTracking.eventIdFB.Purchase, {
        value: dollarMoney,
        currency: currency,
        content_ids: chargeProdId,
        content_name: prodName,
        order_id: orderId
      })

      // 拆单之前会做向下取整，2025/12/18之后改成不向下取整，会把余数算进去
      // GTAG做拆单处理
      const MONEY_BASE = 4.99 // 金额拆分基数,常量
      const splitNum =
        dollarMoney > MONEY_BASE ? Math.ceil(dollarMoney / MONEY_BASE) : 1 // 拆分数量，拆分成n单，向上取整
      for (let i = 1; i <= splitNum; i++) {
        setTimeout(() => {
          let value = 0
          if (splitNum === 1) {
            // 只拆了一次，就取dollarMoney
            value = dollarMoney
          } else {
            // 多次拆单
            if (i === splitNum) {
              // 最后一次，取余数
              value = Number((dollarMoney % MONEY_BASE).toFixed(2))
            } else {
              // 中间，取基数
              value = MONEY_BASE
            }
          }
          console.log(
            `循环次数, ${i};orderId=${orderId}_${i},金额=${value},货币：${currency},商品ID：${chargeProdId},商品名称：${prodName}`
          )
          eventTracking.eventLog(eventTracking.eventIdGtag.purchase, {
            transaction_id: `${orderId}_${i}`,
            value,
            currency: currency,
            items: [
              {
                item_id: chargeProdId as string,
                item_name: prodName as string
              }
            ]
          })
          if (userStore.isNewUser) {
            // 新用户充值埋点
            eventTracking.eventLog(eventTracking.eventId.purchase_new_user, {
              value,
              currency: currency
            })
          }
        }, 1000 * i)
      }
    },
    // 查询完毕删除订单
    deleteOrder(orderId: string) {
      const uid = this.uid
      if (this.rechargeList[uid]) {
        const rechargeFindIndex = this.rechargeList[uid].findIndex(
          (v) => v.orderId === orderId
        )
        if (rechargeFindIndex !== -1) {
          this.rechargeList[uid].splice(rechargeFindIndex, 1)
        }
      }
    }
  },
  persist: {
    key: 'rechargeStore',
    paths: ['rechargeList']
  }
})
